import { RefObject, useEffect } from "react";

import { composedPathContains } from "@/utils/getComposedEventTarget";

/**
 * Close the (non-inline) DetailView panel on a mousedown outside its wrapper.
 *
 * Clicks inside MUI overlays are ignored: the Chat tab's modals (send-test,
 * cancel-schedule, the framework and language choosers — all built on the shared
 * MUI `Modal`) portal to `document.body`, *outside* the panel wrapper. A click
 * inside one would otherwise read as "outside the panel" and close it,
 * unmounting the modal with it. Mirrors ContactLeadPanel.
 */
export const useDetailViewOutsideClose = ({
	ref,
	setShowDetailView,
	showSearchPerson,
	inline,
}: {
	ref: RefObject<HTMLDivElement | null>;
	setShowDetailView: (value: boolean) => void;
	showSearchPerson: boolean;
	inline: boolean;
}): void => {
	useEffect(() => {
		if (inline) {
			return;
		}

		let isMounted = true;

		function handleClickOutside(e: MouseEvent) {
			const target = e.target as HTMLElement | null;

			if (target?.closest?.(".MuiModal-root, .MuiPopover-root, .MuiPopper-root")) {
				return;
			}

			if (e.target && ref.current && !composedPathContains(ref.current, e) && !showSearchPerson) {
				e.stopPropagation();
				e.preventDefault();

				// Use requestAnimationFrame to ensure we're not in the middle of a React render
				requestAnimationFrame(() => {
					if (isMounted) {
						setShowDetailView(false);
					}
				});
			}
		}

		document.addEventListener("mousedown", handleClickOutside);

		return () => {
			isMounted = false;
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [ref, setShowDetailView, showSearchPerson, inline]);
};
